import { useState, useMemo } from "react";

import { Checkbox } from "primereact/checkbox";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import {
  CONTENT_TYPE_OPTIONS_WITH_TESTS,
  difficultyDisplayNames,
  focusDisplayNames,
  RELEASE_OPTIONS,
  languageDisplayNames,
  technologyDisplayNames,
} from "sj-ui-toolkit/src/constants";

import S from "./FilterPanel.module.scss";

// Frontend-stored filter options

const capitalizeAllWords = str =>
  str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const FilterPanel = ({
  isOpen,
  onToggle,
  tags = {},
  filters = {},
  onFilterChange,
  onClearFilters,
  cweTags = [],
}) => {
  const [languageSearch, setLanguageSearch] = useState("");
  const [technologySearch, setTechnologySearch] = useState("");
  const [cweSearch, setCweSearch] = useState("");

  // Filter arrays based on search
  const filteredLanguages = useMemo(() => {
    if (!tags.languages) return [];
    if (!languageSearch) return tags.languages;
    const search = languageSearch.toLowerCase();
    return tags.languages.filter(
      lang =>
        lang.toLowerCase().includes(search) ||
        (languageDisplayNames[lang] || lang).toLowerCase().includes(search)
    );
  }, [tags.languages, languageSearch]);

  const filteredTechnologies = useMemo(() => {
    if (!tags.technologies) return [];
    if (!technologySearch) return tags.technologies;
    const search = technologySearch.toLowerCase();
    return tags.technologies.filter(
      tech =>
        tech.toLowerCase().includes(search) ||
        (technologyDisplayNames[tech] || tech).toLowerCase().includes(search)
    );
  }, [tags.technologies, technologySearch]);

  const filteredCwe = useMemo(() => {
    if (!cweTags || cweTags.length === 0) return [];
    if (!cweSearch) return cweTags;
    const search = cweSearch.toLowerCase();
    return cweTags.filter(
      cwe =>
        cwe.value.toString().includes(search) ||
        cwe.name.toLowerCase().includes(search) ||
        (cwe.description && cwe.description.toLowerCase().includes(search))
    );
  }, [cweTags, cweSearch]);

  // Handle checkbox change
  const handleCheckboxChange = (category, value) => {
    const currentValues = filters[category] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFilterChange(category, newValues);
  };

  // Native checkboxes only toggle on Space; also toggle on Enter for keyboard a11y.
  const handleCheckboxKeyDown = (e, category, value) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCheckboxChange(category, value);
    }
  };

  // Check if a filter is selected
  const isChecked = (category, value) => {
    return (filters[category] || []).includes(value);
  };

  const getCweDisplayName = cweValue => `CWE-${cweValue}`;

  // Calculate active filter count and generate preview chips
  const getActiveFiltersInfo = () => {
    const activeFilters = [];
    let totalCount = 0;

    // Add individual filters (non-grouped)
    ["focus", "difficulty", "release", "contentType"].forEach(category => {
      const values = filters[category] || [];
      values.forEach(value => {
        totalCount++;
        if (activeFilters.length < 5) {
          let label = value;
          if (category === "focus") label = focusDisplayNames[value] || capitalizeAllWords(value);
          if (category === "difficulty") label = difficultyDisplayNames[value] || value;
          if (category === "contentType") {
            const option = CONTENT_TYPE_OPTIONS_WITH_TESTS.find(o => o.value === value);
            label = option ? option.label : value;
          }
          if (category === "release") {
            const option = RELEASE_OPTIONS.find(o => o.value === value);
            label = option ? option.label : value;
          }
          activeFilters.push({ key: `${category}-${value}`, label, category, value });
        }
      });
    });

    // Languages
    const selectedLanguages = filters.languages || [];
    if (selectedLanguages.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedLanguages.length === 1) {
          const lang = selectedLanguages[0];
          activeFilters.push({
            key: `languages-${lang}`,
            label: languageDisplayNames[lang] || capitalizeAllWords(lang),
            category: "languages",
            value: lang,
          });
        } else {
          activeFilters.push({
            key: "languages-group",
            label: `${selectedLanguages.length} Languages`,
            category: "languages",
            isGroup: true,
          });
        }
      }
    }

    // Technologies
    const selectedTechnologies = filters.technologies || [];
    if (selectedTechnologies.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedTechnologies.length === 1) {
          const tech = selectedTechnologies[0];
          activeFilters.push({
            key: `technologies-${tech}`,
            label: technologyDisplayNames[tech] || capitalizeAllWords(tech),
            category: "technologies",
            value: tech,
          });
        } else {
          activeFilters.push({
            key: "technologies-group",
            label: `${selectedTechnologies.length} Technologies`,
            category: "technologies",
            isGroup: true,
          });
        }
      }
    }

    // CWEs
    const selectedCwe = filters.cwe || [];
    if (selectedCwe.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedCwe.length === 1) {
          const cwe = selectedCwe[0];
          activeFilters.push({
            key: `cwe-${cwe}`,
            label: getCweDisplayName(cwe),
            category: "cwe",
            value: cwe,
          });
        } else {
          activeFilters.push({
            key: "cwe-group",
            label: `${selectedCwe.length} CWEs`,
            category: "cwe",
            isGroup: true,
          });
        }
      }
    }

    const remainingCount = totalCount - activeFilters.length;

    return { activeFilters, totalCount, remainingCount };
  };

  const { activeFilters, totalCount, remainingCount } = getActiveFiltersInfo();

  const handleRemoveFilter = filter => {
    if (filter.isGroup) {
      onFilterChange(filter.category, []);
    } else {
      handleCheckboxChange(filter.category, filter.value);
    }
  };

  return (
    <div className={S.filterPanel}>
      <h2 className="sr-only">Filters</h2>
      <div className={S.filterHeader}>
        <button
          className={S.filterToggle}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls="catalog-filter-content"
        >
          <i className={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden="true" />
          <span className={S.filterTitle}>Filters</span>
        </button>

        {totalCount > 0 && (
          <div className={S.filterPreview}>
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={filter.label}
                removable
                onRemove={() => handleRemoveFilter(filter)}
                className={S.previewChip}
              />
            ))}
            {remainingCount > 0 && <span className={S.moreFilters}>+{remainingCount} more</span>}
            <Button label="Clear filters" link className={S.clearButton} onClick={onClearFilters} />
          </div>
        )}
      </div>

      {isOpen && (
        <div
          id="catalog-filter-content"
          className={S.filterContent}
          role="region"
          aria-label="Filter options"
        >
          <div className={S.filterGrid}>
            {/* Audience/Focus */}
            <div className={S.filterSection}>
              <h3 className={S.sectionTitle}>Audience/Focus</h3>
              <div className={S.checkboxList}>
                {(tags.focus || []).map(focus => (
                  <div key={focus} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`focus-${focus}`}
                      checked={isChecked("focus", focus)}
                      onChange={() => handleCheckboxChange("focus", focus)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "focus", focus)}
                    />
                    <label htmlFor={`focus-${focus}`} className={S.checkboxLabel}>
                      {focusDisplayNames[focus] || capitalizeAllWords(focus)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Difficulty */}
            <div className={S.filterSection}>
              <h3 className={S.sectionTitle}>Difficulty</h3>
              <div className={S.checkboxList}>
                {(tags.difficulty || []).map(diff => (
                  <div key={diff} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`difficulty-${diff}`}
                      checked={isChecked("difficulty", diff)}
                      onChange={() => handleCheckboxChange("difficulty", diff)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "difficulty", diff)}
                    />
                    <label htmlFor={`difficulty-${diff}`} className={S.checkboxLabel}>
                      {difficultyDisplayNames[diff] || diff}
                    </label>
                  </div>
                ))}
              </div>

              {/* Release */}
              <h3 className={`${S.sectionTitle} ${S.subSection}`}>Release</h3>
              <div className={S.checkboxList}>
                {RELEASE_OPTIONS.map(option => (
                  <div key={option.value} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`release-${option.value}`}
                      checked={isChecked("release", option.value)}
                      onChange={() => handleCheckboxChange("release", option.value)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "release", option.value)}
                    />
                    <label htmlFor={`release-${option.value}`} className={S.checkboxLabel}>
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Type */}
            <div className={S.filterSection}>
              <h3 className={S.sectionTitle}>Content Type</h3>
              <div className={S.checkboxList}>
                {CONTENT_TYPE_OPTIONS_WITH_TESTS.map(option => (
                  <div key={option.value} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`contentType-${option.value}`}
                      checked={isChecked("contentType", option.value)}
                      onChange={() => handleCheckboxChange("contentType", option.value)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "contentType", option.value)}
                    />
                    <label
                      htmlFor={`contentType-${option.value}`}
                      className={`${S.checkboxLabel} content-type-tooltip-target`}
                      data-pr-tooltip={`${option.label}\n\n${option.description}`}
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
              <Tooltip
                target=".content-type-tooltip-target"
                position="right"
                className={S.cweTooltip}
              />
            </div>

            {/* Language (Searchable) */}
            <div className={S.filterSection}>
              <h3 id="filter-language-title" className={S.sectionTitle}>
                Language
              </h3>
              <div className={S.searchInput}>
                <IconField iconPosition="left">
                  <InputIcon className="pi pi-search" />
                  <InputText
                    value={languageSearch}
                    onChange={e => setLanguageSearch(e.target.value)}
                    placeholder="Search languages"
                    aria-label="Search languages"
                    className={S.filterSearch}
                  />
                </IconField>
              </div>
              <div className={S.checkboxList}>
                {filteredLanguages.map(lang => (
                  <div key={lang} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`language-${lang}`}
                      checked={isChecked("languages", lang)}
                      onChange={() => handleCheckboxChange("languages", lang)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "languages", lang)}
                    />
                    <label htmlFor={`language-${lang}`} className={S.checkboxLabel}>
                      {languageDisplayNames[lang] || capitalizeAllWords(lang)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Technology (Searchable) */}
            <div className={S.filterSection}>
              <h3 id="filter-technology-title" className={S.sectionTitle}>
                Technology
              </h3>
              <div className={S.searchInput}>
                <IconField iconPosition="left">
                  <InputIcon className="pi pi-search" />
                  <InputText
                    value={technologySearch}
                    onChange={e => setTechnologySearch(e.target.value)}
                    placeholder="Search technologies"
                    aria-label="Search technologies"
                    className={S.filterSearch}
                  />
                </IconField>
              </div>
              <div className={S.checkboxList}>
                {filteredTechnologies.map(tech => (
                  <div key={tech} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`technology-${tech}`}
                      checked={isChecked("technologies", tech)}
                      onChange={() => handleCheckboxChange("technologies", tech)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "technologies", tech)}
                    />
                    <label htmlFor={`technology-${tech}`} className={S.checkboxLabel}>
                      {technologyDisplayNames[tech] || capitalizeAllWords(tech)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* CWE (Searchable) - Updated with tooltips */}
            <div className={S.filterSection}>
              <h3 id="filter-cwe-title" className={S.sectionTitle}>
                <abbr title="Common Weakness Enumeration" className={S.abbrTitle}>
                  CWE
                </abbr>
              </h3>
              <div className={S.searchInput}>
                <IconField iconPosition="left">
                  <InputIcon className="pi pi-search" />
                  <InputText
                    value={cweSearch}
                    onChange={e => setCweSearch(e.target.value)}
                    placeholder="Search by CWE number or name"
                    aria-label="Search by CWE number or name"
                    className={S.filterSearch}
                  />
                </IconField>
              </div>
              <div className={S.checkboxList}>
                {filteredCwe.map(cwe => (
                  <div key={cwe.value} className={S.checkboxItem}>
                    <Checkbox
                      inputId={`cwe-${cwe.value}`}
                      checked={isChecked("cwe", cwe.value)}
                      onChange={() => handleCheckboxChange("cwe", cwe.value)}
                      onKeyDown={e => handleCheckboxKeyDown(e, "cwe", cwe.value)}
                    />
                    <label
                      htmlFor={`cwe-${cwe.value}`}
                      className={`${S.checkboxLabel} ${S.cweLabel} cwe-tooltip-target`}
                      data-pr-tooltip={
                        cwe.description ? `${cwe.name}\n\n${cwe.description}` : cwe.name
                      }
                    >
                      {cwe.name}
                    </label>
                  </div>
                ))}
              </div>
              <Tooltip target=".cwe-tooltip-target" position="left" className={S.cweTooltip} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
